import { isAEMAuthorMode } from '../../utils/aemAuthorMode'
import { debounce } from '../../utils/debounce'
import { throttle } from '../../utils/throttle'

class GMXBrightcoveVideoApiLoader {
	#loadStatus = ''
	get loadStatus(): string {
		return this.#loadStatus
	}

	set loadStatus(val: string) {
		this.#loadStatus = val
	}

	constructor() {
		if (!window.gmxBrightcoveVideoApiLoader) {
			window.gmxBrightcoveVideoApiLoader = this
		} else {
			window.console.log('Cannot instantiate more that one BC Api Loader')
		}
	}

	public loadBCVideoApi(accountId: string, playerId: string): void {
		const sanitizedAccountId = this._sanitizeId(accountId)
		const sanitizedPlayerId = this._sanitizeId(playerId)

		if (this.loadStatus === 'loaded') {
			this.apiLoadedCallback()
		} else if (this.loadStatus !== 'loading') {
			const newScript = document.createElement('script')
			newScript.src = `//players.brightcove.net/${sanitizedAccountId}/${sanitizedPlayerId}_default/index.min.js`
			newScript.type = 'text/javascript'
			newScript.async = true
			newScript.defer = true
			this.loadStatus = 'loading'
			document.body.appendChild(newScript)

			newScript.onload = () => {
				this.apiLoadedCallback()
			}
		}
	}

	public apiLoadedCallback(): void {
		window.dispatchEvent(new CustomEvent('gmxBrightCoveVideoApiLoaded'))
		this.loadStatus = 'loaded'
	}

	private _sanitizeId(id: string): string {
		if (/^[a-zA-Z0-9]+$/.test(id)) {
			return id
		} else {
			window.console.log(`ERROR: Invalid ID: {$id}`)
			return ''
		}
	}
}

const gmxBrightcoveVideoApiLoader = Object.freeze(new GMXBrightcoveVideoApiLoader())
export default gmxBrightcoveVideoApiLoader

/**
 * Video Brightcove
 */
export class GMXVideoBrightcove extends HTMLElement {
	videoContainerElement: HTMLElement
	videoElement: HTMLElement
	playPauseToggleBtn: HTMLButtonElement
	intersectionObserver: IntersectionObserver
	playbackIntersectionObserver: IntersectionObserver
	parentExpandTileEl: HTMLElement
	parentCarouselEl: HTMLElement
	posterframeEl: HTMLElement | null

	private uniquePlayerInstanceId: string
	private canRestartAutoplayVidOnEntry = true
	private pendingPlayPromise: Promise<void> | null = null
	private playRequestId = 0
	private playbackIntent: 'play' | 'pause' = 'pause'

	// @ts-expect-error:  There is no Brightcove TS interface
	private videoPlayer

	private boundPlayPauseToggleBtnCallback = this._handlePlayPauseClick.bind(this)
	private boundHandleObservationCallback = this.handleObservation.bind(this)
	private boundHandlePlaybackObservationCallback = this.handlePlaybackObservation.bind(this)
	private boundHandleVideoEndedCallback = this._handleVideoEnded.bind(this)
	private boundHandleVideoPlayCallback = this._handleVideoPlay.bind(this)
	private boundHandleVideoPauseCallback = this._handleVideoPause.bind(this)
	private boundHandleVideoLoadedMetadataCallback = this._handleVideoLoadedMetadata.bind(this)
	private boundHandleParentExpandTileExpanded = this._handleParentExpandTileExpanded.bind(this)
	private boundHandleCarouselSlideChangeCallback = this._handleCarouselSlideChange.bind(this)
	private boundHandleCarouselDraggingCallback = throttle(this._handleCarouselDragging.bind(this), 500)
	private boundHandleWindowResize = debounce(this._handleWindowResize.bind(this), 250)

	get accountId(): string {
		return this.getAttribute('data-account-id')
	}

	get playerId(): string {
		return this.getAttribute('data-player-id')
	}

	get videoId(): string {
		return this.getAttribute('data-video-id')
	}

	get mobileVideoId(): string {
		return this.getAttribute('data-mobile-video-id')
	}

	get isAutoplay(): boolean {
		return this.getAttribute('data-is-autoplay') === 'true'
	}

	get isLoop(): boolean {
		return this.getAttribute('data-is-loop') === 'true'
	}

	get isShowControls(): boolean {
		return this.getAttribute('data-show-controls') === 'true'
	}

	constructor() {
		super()

		this.uniquePlayerInstanceId = `bcPlayerID-${this.playerId}-${this.accountId}-${Math.round(
			Math.random() * 10000
		)}`
	}

	connectedCallback(): void {
		// Lazy Load:  Setup an observer to watch when the component enters the viewport - 500px root margin
		this.intersectionObserver = new IntersectionObserver(this.boundHandleObservationCallback, {
			root: null,
			rootMargin: '500px',
			threshold: 0,
		})

		this.intersectionObserver.observe(this)
	}

	disconnectedCallback(): void {
		if (this.parentExpandTileEl) {
			this.parentExpandTileEl.removeEventListener('gmxTileOpening', this.boundHandleParentExpandTileExpanded)
		}

		if (this.parentCarouselEl) {
			this.parentCarouselEl.removeEventListener(
				'gmxCarouselSlideChange',
				this.boundHandleCarouselSlideChangeCallback
			)

			this.parentCarouselEl.removeEventListener('gmxCarouselDragging', this.boundHandleCarouselDraggingCallback)
		}

		if (this.playPauseToggleBtn) {
			this.playPauseToggleBtn.removeEventListener('click', this.boundPlayPauseToggleBtnCallback)
		}

		if (this.intersectionObserver) {
			this.intersectionObserver.unobserve(this)
		}

		if (this.playbackIntersectionObserver) {
			this.playbackIntersectionObserver.unobserve(this)
		}

		window.removeEventListener('resize', this.boundHandleWindowResize)
	}

	handleObservation(entries: IntersectionObserverEntry[]): void {
		const entry = entries[0]

		if (entry.isIntersecting) {
			// No need to load the bc player if in author mode
			if (!isAEMAuthorMode()) {
				this.loadBCApis()
			}
			this.intersectionObserver.unobserve(this)
		}
	}

	handlePlaybackObservation(entries: IntersectionObserverEntry[]): void {
		const entry = entries[0]

		if (this.videoPlayer) {
			if (entry.isIntersecting) {
				// Intent, not paused(), so scrolling back in cancels a pause queued behind a pending play
				if (this.isAutoplay && this.canRestartAutoplayVidOnEntry && this.playbackIntent === 'pause') {
					this.playVideo()
				}
			} else {
				// A queued play reports paused() until the player is ready, so it needs pausing too
				if (!this.videoPlayer.paused() || this.pendingPlayPromise) {
					this.pauseVideo()
				}
			}
		}
	}

	loadBCApis(): void {
		// @ts-expect-error:  There is no brightcove TS interface
		if (window.bc) {
			// Player already loaded
			this.onBcApiLoaded()
		} else {
			if (window.gmxBrightcoveVideoApiLoader?.loadStatus !== 'loaded') {
				window.addEventListener('gmxBrightCoveVideoApiLoaded', this.onBcApiLoaded.bind(this), { once: true })

				window.gmxBrightcoveVideoApiLoader.loadBCVideoApi(this.accountId, this.playerId)
			} else {
				this.onBcApiLoaded()
			}
		}
	}

	onBcApiLoaded(): void {
		this.initVideo()

		window.dispatchEvent(new Event('gmxBCAPILoaded'))

		// @ts-expect-error:  There is no brightcove TS interface
		this.videoPlayer = window.bc(this.uniquePlayerInstanceId)

		this.videoPlayer.loop(this.isLoop)
		this.videoPlayer.controls(this.isShowControls)
		this.videoPlayer.muted(this.isAutoplay)
		this.videoPlayer.preload('metadata')

		this.videoPlayer.on('ended', this.boundHandleVideoEndedCallback)
		this.videoPlayer.on('play', this.boundHandleVideoPlayCallback)
		this.videoPlayer.on('pause', this.boundHandleVideoPauseCallback)
		// loadedmetadata is when play() is safe; canplay/loadeddata often never fire with preload('metadata')
		this.videoPlayer.on('loadedmetadata', this.boundHandleVideoLoadedMetadataCallback)

		this.videoPlayer.ready(() => {
			this._setupPlaybackIntersectionObserver()
		})
	}

	/**
	 * Show the play/pause toggle once metadata is loaded and play() is safe to call.
	 * Hidden again while native controls are up and the video is already playing.
	 * @private
	 */
	private _handleVideoLoadedMetadata(): void {
		const shouldShowToggle = !this.isShowControls || Boolean(this.videoPlayer?.paused())
		this.playPauseToggleBtn?.classList.toggle('show', shouldShowToggle)
	}

	private _setupPlaybackIntersectionObserver(): void {
		this.playbackIntersectionObserver = new IntersectionObserver(this.boundHandlePlaybackObservationCallback, {
			root: null,
			rootMargin: '0px',
			threshold: 0.5,
		})

		this.playbackIntersectionObserver.observe(this)
	}

	/**
	 * Initialize the component
	 */
	initVideo(): void {
		this.posterframeEl = this.querySelector('.posterframe')
		this.playPauseToggleBtn = this.querySelector('.play-pause-toggle')

		this.videoContainerElement = this.querySelector('.video-container')
		if (!this.videoContainerElement) {
			this.videoContainerElement = document.createElement('div')
			this.videoContainerElement.classList.add('video-container')

			if (this.posterframeEl) {
				this.videoContainerElement.appendChild(this.posterframeEl)
			}

			if (this.playPauseToggleBtn) {
				this.videoContainerElement.appendChild(this.playPauseToggleBtn)
			}

			this.prepend(this.videoContainerElement)
		}

		this.parentExpandTileEl = this.closest('div.collapsed-content-container')?.closest('gmx-expand-tile-base')
		if (this.parentExpandTileEl) {
			this.parentExpandTileEl.addEventListener('gmxTileOpening', this.boundHandleParentExpandTileExpanded)
		}

		this.parentCarouselEl = this.closest('gmx-carousel')
		if (this.parentCarouselEl) {
			this.parentCarouselEl.addEventListener(
				'gmxCarouselSlideChange',
				this.boundHandleCarouselSlideChangeCallback
			)

			this.parentCarouselEl.addEventListener('gmxCarouselDragging', this.boundHandleCarouselDraggingCallback)
		}

		if (this.playPauseToggleBtn) {
			this.playPauseToggleBtn.addEventListener('click', this.boundPlayPauseToggleBtnCallback)

			// Default to not playing
			this.playPauseToggleBtn.setAttribute('aria-pressed', 'false')
		}

		if (!this.videoElement) {
			this.createVideoElement()
		}
	}

	createVideoElement(): void {
		this.videoElement = document.createElement('video-js')
		this.videoElement.classList.add('vjs-fluid')

		this.videoElement.setAttribute('id', this.uniquePlayerInstanceId)
		this.videoElement.setAttribute('data-account-id', this.accountId)
		this.videoElement.setAttribute('data-player-id', this.playerId)
		this.videoElement.setAttribute('data-embed', 'default')
		this.videoElement.setAttribute('playsinline', '')
		this.videoElement.setAttribute('data-video-id', this._getVideoIdForCurrentViewport())

		if (this.isAutoplay) {
			// No posterframe if autoplay
			this.videoElement.setAttribute('poster', '')
		}

		this.videoElement.classList.add('video-js')

		this.videoContainerElement.prepend(this.videoElement)

		window.addEventListener('resize', this.boundHandleWindowResize)
	}

	/**
	 * Function to trigger the video to play
	 */
	playVideo(): void {
		if (!this.videoPlayer) {
			return
		}

		this.playbackIntent = 'play'
		const requestId = ++this.playRequestId

		// Older player tech returns undefined instead of a promise
		this.pendingPlayPromise = Promise.resolve(this.videoPlayer.play())
			.then(() => {
				if (this.posterframeEl) {
					this.posterframeEl.classList.add('hide')
				}
			})
			.catch(() => {
				// Interrupted or failed before playback began, e.g. the user scrolled away.
				// Clearing the intent lets a later scroll into view retry.
				if (requestId === this.playRequestId) {
					this.playbackIntent = 'pause'
				}
			})
			.finally(() => {
				// A newer request may have replaced this one while it was in flight
				if (requestId === this.playRequestId) {
					this.pendingPlayPromise = null
				}
			})
	}

	/**
	 * Function to trigger the video to pause
	 */
	pauseVideo(): void {
		if (!this.videoPlayer) {
			return
		}

		this.playbackIntent = 'pause'
		this.videoPlayer.pause()

		// The pause above aborts an in-flight play, which playVideo() swallows. It is a no-op
		// on a play video.js has queued but not started, so pause again once that one settles.
		if (this.pendingPlayPromise) {
			this.pendingPlayPromise.then(() => {
				if (this.playbackIntent === 'pause' && this.videoPlayer) {
					this.videoPlayer.pause()
				}
			})
		}
	}

	/**
	 * Callack for when the play/pause toggle is clicked
	 * @param e {event}
	 * @private
	 */
	private _handlePlayPauseClick(e: Event): void {
		e.stopPropagation()

		if (!this.videoPlayer) {
			return
		}

		// A play still in flight reports paused() as false, so trust the last request instead
		const isPaused = this.pendingPlayPromise ? this.playbackIntent === 'pause' : this.videoPlayer.paused()

		if (isPaused) {
			this.canRestartAutoplayVidOnEntry = true
			this.playVideo()
		} else {
			this.canRestartAutoplayVidOnEntry = false
			this.pauseVideo()
		}
	}

	/**
	 * Callback triggered when the video has ended
	 * @private
	 */
	private _handleVideoEnded(): void {
		if (!this.isLoop) {
			// Without this an autoplay video that ran to completion never restarts on re-entry
			this.playbackIntent = 'pause'
			this.removeAttribute('data-playing')

			if (this.playPauseToggleBtn) {
				this.playPauseToggleBtn.setAttribute('aria-pressed', 'false')
				this.playPauseToggleBtn.setAttribute('data-icon-name', 'play')
			}
		}
	}

	/**
	 * Callback for when the video player triggers a 'play' event
	 * @private
	 */
	private _handleVideoPlay(): void {
		this.setAttribute('data-playing', '')

		if (this.playPauseToggleBtn) {
			this.playPauseToggleBtn.setAttribute('aria-pressed', 'true')
			this.playPauseToggleBtn.setAttribute('data-icon-name', 'pause')

			if (this.isShowControls) {
				this.playPauseToggleBtn.classList.remove('show')
			}
		}
	}

	/**
	 * Callback for when the video player triggers a 'pause' event
	 * @private
	 */
	private _handleVideoPause(): void {
		// Catches pauses we didn't request, e.g. the control bar. A pause queued behind an
		// in-flight play is excluded so it doesn't overwrite the request that is still running.
		if (!this.pendingPlayPromise) {
			this.playbackIntent = 'pause'
		}

		this.removeAttribute('data-playing')

		if (this.playPauseToggleBtn) {
			this.playPauseToggleBtn.setAttribute('aria-pressed', 'false')
			this.playPauseToggleBtn.setAttribute('data-icon-name', 'play')
		}
	}

	/**
	 * Callback to handle if/when video is in an expand tile collapsed container and the tile gets opened/expanded.
	 * @private
	 */
	private _handleParentExpandTileExpanded(): void {
		this.pauseVideo()
	}

	/**
	 * Callback to handle if/when video in a carousel catches a slide change event.
	 * @private
	 */
	private _handleCarouselSlideChange(e: CustomEvent): void {
		const eventDetails = e.detail
		const parentCarouselSlideNum = parseInt(this.closest('.slide')?.getAttribute('data-slide-num'), 10)

		// Want to pause a video if the parent carousel slide changes
		if (eventDetails.slideNum !== parentCarouselSlideNum) {
			this.pauseVideo()
		}
	}

	private _handleCarouselDragging(): void {
		if (this.videoPlayer && (!this.videoPlayer.paused() || this.pendingPlayPromise)) {
			this.pauseVideo()
		}
	}

	private _getVideoIdForCurrentViewport(): string {
		return this.isMobile() && this.mobileVideoId ? this.mobileVideoId : this.videoId
	}

	private _handleWindowResize(): void {
		if (!this.videoPlayer) {
			return
		}

		const currentVideoId = this.videoPlayer.mediainfo?.id
		const viewportVideoId = this._getVideoIdForCurrentViewport()

		if (currentVideoId !== viewportVideoId) {
			this.pauseVideo()

			// @ts-expect-error:  BC TS interface missing
			this.videoPlayer.catalog.getVideo(viewportVideoId, (error, video) => {
				if (!error) {
					this.videoElement.setAttribute('data-video-id', viewportVideoId)

					this.playPauseToggleBtn?.classList.remove('show')
					this.videoPlayer.catalog.load(video)

					if (this.isAutoplay) {
						this.playVideo()
					}
				}
			})
		}
	}

	isMobile(): boolean {
		return !window.matchMedia('screen and (min-width: 481px)').matches
	}
}

if (!customElements.get('gmx-video-brightcove')) {
	customElements.define('gmx-video-brightcove', GMXVideoBrightcove)
}
